<template>
  <h5 class="card-title mb-2 fw-bold fs-3 ms-5">Setup Your Loyalty Program</h5>
  <div class="d-flex align-items-center mb-3">
    <div class="step-number">1</div>
    <div class="ms-3">
      <p class="mb-0">On average, how much do customers spend at your store?</p>
    </div>
  </div>
  <div class="d-flex flex-row mt-3 pb-4 align-items-center">
    <div class="d-flex flex-column ms-5">
      <span class="pe-2 bold-text body-text">
        {{ currencySymbol(store.brandCurrency) }}
      </span>
    </div>
    <div class="d-flex flex-column col-6 pe-2">
      <div class="input-container h-100">
        <CurrencyInput
          class="form-control"
          v-model="store.customerSpend"
          :max-length="999999999"
          :min-length="0"
          :class="{
            'is-invalid': !store.customerSpend,
            'is-valid': store.customerSpend,
          }"
        />
      </div>
    </div>
    <div class="d-flex flex-column">
      <span class="bold-text">{{ store.customerSpendMode }}</span>
    </div>
    <div v-if="isRestaurantIndustry" class="d-flex flex-column">
      <div class="dropdown">
        <button
          class="btn btn-dropdown dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        ></button>
        <ul class="dropdown-menu">
          <li v-for="option in modeOptions" :key="option.value">
            <a
              class="dropdown-item body-text"
              @click="handleOptionChange(option.label)"
            >
              {{ option.label }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div class="d-flex align-items-center mt-4 mb-3">
    <div class="step-number">2</div>
    <div class="ms-3">
      <p class="mb-0">Loyalty Program Type</p>
    </div>
  </div>
  <div class="d-flex flex-row mt-3 mb-4 gap-3 ms-5">
    <div
      class="loyalty-selection-tab"
      :class="{ selected: store.loyaltyProgramType === 'stamps' }"
      @click="changeLoyaltyType('stamps')"
    >
      <div
        class="radio-button"
        :class="{ 'radio-selected': store.loyaltyProgramType === 'stamps' }"
      ></div>
      <span>Loyalty Stamp Card</span>
      <img
        :src="StampIcon"
        alt="Loyalty Stamp Card Icon"
        class="ms-auto icon-small"
      />
    </div>

    <div
      class="loyalty-selection-tab"
      :class="{ selected: store.loyaltyProgramType === 'points' }"
      @click="changeLoyaltyType('points')"
    >
      <div
        class="radio-button"
        :class="{ 'radio-selected': store.loyaltyProgramType === 'points' }"
      ></div>
      <span>Loyalty Points</span>
      <img
        :src="PointsIcon"
        alt="Loyalty Points Icon"
        class="ms-auto icon-small"
      />
    </div>
  </div>
  <div
    v-if="store.loyaltyProgramType === 'stamps'"
    class="loyalty-details-container"
  >
    <div class="loyalty-type-description ms-4">
      <h5 class="fw-bold mb-3">Loyalty Stamp Cards</h5>
      <div class="ms-4">
        <ul>
          <li>Easy to understand – Collect stamps, get a reward.</li>
          <li>Encourages repeat visits with a clear goal.</li>
          <li>No need to track points – simple for customers and staff.</li>
          <li>Great for small purchases (e.g., coffee, snacks).</li>
        </ul>
      </div>
      <p class="mt-2">
        <i class="bi bi-gift"></i> Only redeem 1 reward at a time
      </p>
    </div>
    <div class="placeholder-card-image mt-2 ms-4">
      <StampCard :card-settings="stampCardSettings" style="zoom: 0.8" />
    </div>
  </div>

  <div
    v-else-if="store.loyaltyProgramType === 'points'"
    class="loyalty-details-container"
  >
    <div class="loyalty-type-description ms-4">
      <h5 class="fw-bold mb-3">Loyalty Points</h5>
      <div class="ms-4">
        <ul>
          <li>Flexible rewards – Earn points and redeem various rewards.</li>
          <li>Set up different types of rewards to be redeemed.</li>
          <li>Encourages higher spending with tiered rewards.</li>
        </ul>
      </div>
      <p class="mt-2">
        <i class="bi bi-gift"></i> Redeem multiple types of rewards
      </p>
    </div>
    <div class="placeholder-card-image ms-4">
      <img :src="PointsImage" alt="Loyalty Points Preview" class="img-fluid" />
    </div>
  </div>

  <div class="alert alert-warning mt-4 p-2 ms-5" role="alert">
    <i class="bi bi-exclamation-triangle-fill"></i>
    30-Day Lock: Once selected, you CANNOT SWITCH LOYALTY PROGRAM TYPE for 30
    days. Some features and settings may also be adjusted based on the program
    type selected.
  </div>
</template>

<script setup>
import PointsImage from "@/pages/AccountManagement/UserOnboarding/Icons/PointsCard.png";
import StampIcon from "@/pages/AccountManagement/UserOnboarding/Icons/ICO127.svg";
import PointsIcon from "@/pages/AccountManagement/UserOnboarding/Icons/ICO63.svg";
import StampCard from "@/reusables/stampCard/StampCard.vue";
import { useBrandSetupStore } from "@/stores/brand";
import { computed, watch } from "vue";
import { formatDate } from "@/constants/dateUtils";
import { useQuery } from "@urql/vue";
import { GET_CURRENCY_RATE } from "@/constants/graphql";
import CurrencyInput from "@/reusables/CurrencyInput.vue";
import { getCurrencyData } from "country-currency-utils";
import { currencySymbol } from "@/constants/country_currency";
import { useRewardSetupStore } from "@/stores/rewardSetup";

const store = useBrandSetupStore();
const rewardStore = useRewardSetupStore();

const isRestaurantIndustry = computed(() => {
  return store.brandIndustry.name === "Restaurant";
});

const modeOptions = [
  { label: "per person", value: "per_person" },
  { label: "per transaction", value: "per_transaction" },
];

const handleOptionChange = (value) => {
  store.customerSpendMode = value;
};

const currencyRateQuery = useQuery({
  query: GET_CURRENCY_RATE,
  variables: {
    countryCode: store.brandCountry.code,
    industryId: store.brandIndustry.id,
  },
});

watch(currencyRateQuery.fetching, (fetching) => {
  if (!fetching) {
    const data = currencyRateQuery.data.value?.currencyRateDefaults;

    store.$patch({
      brandPoints: data?.point || 1,
      brandSpending: data?.currency || 50,
    });
  }
});

const stampCardSettings = computed(() => {
  const today = new Date();
  const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1));

  return {
    description: `BUY ${store.totalStamps || 8} & GET 1 FREE`,
    backgroundColor: "#000",
    textColor: "#fff",
    stampIcon: "✓",
    stampImageUrl: "",
    beforeStampsBackgroundColor: "#fff",
    beforeStampsTextColor: "#000",
    reward: "FREE ITEM",
    rewardEmoji: "",
    rewardImageUrl: "",
    footer: "1 STAMP FOR EVERY RM50 SPENT",
    version: `V.${formatDate(new Date(), "dd.MM.yyyy")}`,
    dateCollected: [
      formatDate(today, "yyyy-MM-dd"),
      formatDate(tomorrow, "yyyy-MM-dd"),
    ],
    totalStamps: store.totalStamps || 8,
    rewardBackgroundColor: "#fff",
    rewardTextColor: "#000",
  };
});

const changeLoyaltyType = (type) => {
  store.loyaltyProgramType = type;
  if (type === "stamps") {
    rewardStore.$patch({
      rewardExpiry: 30,
    });
  } else {
    rewardStore.$patch({
      rewardExpiry: 0,
    });
  }
};
</script>

<style scoped>
.btn-dropdown {
  background-color: white;
  border-color: white;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  width: 100%;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  font-size: 14px;
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
}

.dropdown-item {
  padding: 0.25rem 1rem;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.continue-row {
  margin-top: auto;
}

.body-text {
  font-size: 1rem !important;
}
.btn-dropdown {
  width: 5px;
}

.loyalty-selection-tab {
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 1rem;
  padding: 1rem;
  cursor: pointer;
  flex: 1;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.loyalty-selection-tab.selected {
  border-color: black;
  box-shadow: 0 0 8px black;
}

.loyalty-selection-tab:hover {
  border-color: black;
  box-shadow: 0 0 8px black;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #ced4da;
  border-radius: 50%;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.radio-button.radio-selected {
  border-color: black;
  background-color: black;
}

.radio-button.radio-selected::after {
  content: "";
  width: 10px;
  height: 10px;
  background-color: white;
  border-radius: 50%;
}

.loyalty-selection-tab span {
  font-weight: bold;
}

.loyalty-details-container {
  padding: 1.5rem;
  margin-top: 1rem;
}

.loyalty-type-description ul {
  list-style: none;
  padding-left: 0;
  font-size: 0.95rem;
  color: #495057;
}

.loyalty-type-description ul li:before {
  content: "• ";
  color: black;
  font-weight: bold;
  display: inline-block;
  width: 1em;
  margin-left: -1em;
}

.placeholder-card-image {
  margin-top: 1.5rem;
  text-align: center;
}

.placeholder-card-image img {
  height: auto;
  border: 1px solid #eee;
  border-radius: 0.25rem;
}

.placeholder-card-image :deep(.stamp-card) {
  max-width: 100%;
  height: auto;
  border: 1px solid #eee;
  border-radius: 0.25rem;
}

.alert-warning {
  background-color: white;
  border-color: white;
  border: 1px solid #ced4da;
  border-radius: 1rem;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  color: #ff0000;
  font-size: 14px;
  bottom: 20px;
}

.alert-warning .bi {
  margin-right: 0.5rem;
}

.icon-small {
  width: 24px;
  height: 24px;
  margin-left: 10px;
}
.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: black;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
}

.mb-0 {
  margin-bottom: 0 !important;
}
</style>
